import { InputField } from '@/components/CommonComponents/InputField';
import ColorPicker from '@/components/CommonComponents/ColorPicker';
import { generateEmojiList } from '@/helper/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

interface BasicInformationProps {
  projectName: string;
  setProjectName: (val: string) => void;
  primaryColor: string;
  setPrimaryColor: (value: string) => void;
  secondaryColor: string;
  setSecondaryColor: (value: string) => void;
  selectedEmoji: string;
  setSelectedEmoji: (emoji: string) => void;
  description: string;
  setDescription: (value: string) => void;
  onValidationChange?: (isValid: boolean) => void;
  isEditMode: boolean;
}

const BasicInformation: React.FC<BasicInformationProps> = ({
  projectName,
  setProjectName,
  primaryColor,
  setPrimaryColor,
  secondaryColor,
  setSecondaryColor,
  selectedEmoji,
  setSelectedEmoji,
  description,
  setDescription,
  onValidationChange,
  isEditMode,
}) => {
  const emojiList = useMemo(() => generateEmojiList(), []);

  const [startIndex, setStartIndex] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const visibleCount = 12;

  const handleNext = () => {
    setStartIndex((prev) => (prev + visibleCount) % emojiList.length);
  };

  const handlePrev = () => {
    setStartIndex((prev) =>
      prev - visibleCount < 0 ? emojiList.length - visibleCount : prev - visibleCount,
    );
  };

  const isValidHex = (value: string) => /^#?[0-9A-Fa-f]{3}([0-9A-Fa-f]{3})?$/.test(value);

  const validateStep = () => {
    const newErrors: Record<string, string> = {};

    if (projectName && (projectName.length < 5 || projectName.length > 60)) {
      newErrors.projectName = 'Project Name must be between 5 and 60 characters.';
    }
    if (description && (description.length < 10 || description.length > 500)) {
      newErrors.description = 'Project Description must be between 10 and 500 characters.';
    }
    if (primaryColor && !isValidHex(primaryColor)) {
      newErrors.primaryColor = 'Primary Color must be a valid hex code.';
    }
    if (secondaryColor && !isValidHex(secondaryColor)) {
      newErrors.secondaryColor = 'Secondary Color must be a valid hex code.';
    }

    if (!selectedEmoji || selectedEmoji.trim() === '') {
      newErrors.selectedEmoji = 'Please select an emoji.';
    }
    setErrors(newErrors);
    const isValid =
      projectName?.trim().length > 0 &&
      description?.trim().length > 0 &&
      selectedEmoji?.trim().length > 0 &&
      Object.keys(newErrors).length === 0;
    onValidationChange?.(isValid);
    return isValid;
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      const valid = validateStep();
      onValidationChange?.(valid);
    }, 500);

    return () => clearTimeout(handler);
  }, [projectName, description, primaryColor, secondaryColor, validateStep]);

  let visibleEmojis = emojiList.slice(startIndex, startIndex + visibleCount);
  if (selectedEmoji && !visibleEmojis.some((item) => item.symbol === selectedEmoji)) {
    const selectedEmojiObj = emojiList.find((item) => item.symbol === selectedEmoji);
    if (selectedEmojiObj) {
      visibleEmojis = [selectedEmojiObj, ...visibleEmojis];
    }
  }

  return (
    <div className="mx-auto text-[#621C43]">
      {/* Header */}
      <div className="border-b border-b-[#621C43] px-5 py-6 flex flex-col">
        <h2 className="text-xl font-bold">Basic Information</h2>
        <p className="text-base font-medium">
          Enter the basic details for the {isEditMode ? '' : 'new'} project
        </p>
      </div>
      {/* {error && <p className="text-red-500 text-sm font-medium px-6">{error}</p>} */}

      {/* Project Fields */}
      <div className="grid grid-cols-1 md:grid-cols-1 px-6 space-y-2 py-2">
        {/* Project Name */}
        <InputField
          label="Project Name"
          required
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          placeholder="Enter Project Name"
        />
        {errors.projectName && (
          <p className="text-red-500 text-sm font-medium">{errors.projectName}</p>
        )}

        {/* Project Description */}
        <InputField
          label="Project Description"
          textarea
          required
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Enter project description"
          showCharCount
        />
        {errors.description && (
          <p className="text-red-500 text-sm font-medium">{errors.description}</p>
        )}

        {/* Colors */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ColorPicker
            label="Primary Color"
            value={primaryColor}
            onChange={setPrimaryColor}
            error={errors.primaryColor}
          />

          <ColorPicker
            label="Secondary Color"
            value={secondaryColor}
            onChange={setSecondaryColor}
            error={errors.secondaryColor}
          />
        </div>

        {/* Emoji Carousel */}
        <div>
          <label className=" text-sm font-medium mb-1 text-[#621C43]">
            Choose an emoji <span className="text-red-500">*</span>
          </label>
          <div className="mb-6 px-4 py-2 flex justify-between items-center bg-[#4A082D33] rounded-lg">
            <button onClick={handlePrev} className="p-2 border rounded-full hover:bg-gray-100">
              <ChevronLeft size={20} />
            </button>

            {/* Emoji container */}
            <div className="flex gap-3">
              {visibleEmojis.map((item) => (
                <button
                  key={item.code}
                  type="button"
                  onClick={() => setSelectedEmoji(item.symbol)}
                  className={`text-4xl px-3 py-2 rounded-lg border flex flex-col items-center bg-[#927385] ${
                    selectedEmoji === item.symbol
                      ? 'border-[#AD0C62] bg-[white] border-[2px]'
                      : ' hover:border-pink-300'
                  }`}
                >
                  {item.symbol}
                </button>
              ))}
            </div>
            <button onClick={handleNext} className="p-2 border rounded-full hover:bg-gray-100">
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicInformation;
